import type { ButtonHTMLAttributes, ReactNode } from "react"
import { cn } from "@/lib/format"

export function Karte({ titel, unter, rechts, children, className }: {
  titel?: ReactNode
  unter?: ReactNode
  rechts?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn("grid min-w-0 content-start gap-3 rounded-[10px] border border-hair/10 bg-surface px-[18px] pb-[18px] pt-4", className)}>
      {(titel || rechts) && (
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            {titel && <h2 className="text-[15px] font-semibold [text-wrap:balance]">{titel}</h2>}
            {unter && <p className="mt-0.5 max-w-[70ch] text-[12.5px] text-muted">{unter}</p>}
          </div>
          {rechts}
        </div>
      )}
      {children}
    </section>
  )
}

export function Chip({ an, children, ...props }: { an: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={an}
      {...props}
      className={cn(
        "rounded-full border px-2.5 py-1.5 text-[12.5px] font-medium leading-none transition-colors",
        an
          ? "border-accent bg-accent/10 text-accent-strong"
          : "border-hair/10 bg-surface text-ink2 hover:border-axis hover:text-ink",
        props.className,
      )}
    >
      {children}
    </button>
  )
}

export function Segment<T extends string>({ werte, wert, onChange, label }: {
  werte: Array<{ id: T; name: string; titel?: string }>
  wert: T | null
  onChange: (id: T) => void
  label?: string
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex overflow-hidden rounded-lg border border-hair/10 bg-surface">
      {werte.map((w, i) => (
        <button
          key={w.id}
          type="button"
          title={w.titel}
          aria-pressed={wert === w.id}
          onClick={() => onChange(w.id)}
          className={cn(
            "px-3 py-1.5 text-[12.5px] font-medium leading-none",
            i > 0 && "border-l border-hair/10",
            wert === w.id ? "bg-accent/10 text-accent-strong" : "text-ink2 hover:bg-surface2 hover:text-ink",
          )}
        >
          {w.name}
        </button>
      ))}
    </div>
  )
}

export function FilterLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mr-0.5 font-mono text-[10.5px] font-medium uppercase leading-none tracking-[0.08em] text-muted">
      {children}
    </label>
  )
}

export function Leer({ children = "Keine Proben für diese Auswahl. Filter lockern oder „Alle Daten“ wählen." }: { children?: ReactNode }) {
  return <div className="px-2 py-7 text-center text-[13px] text-muted">{children}</div>
}

export function Knopf({ variante = "standard", className, ...props }: { variante?: "standard" | "primaer" | "gefahr" | "link" } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50",
        variante === "standard" && "border border-hair/15 bg-surface px-3 py-2 text-ink hover:bg-surface2",
        variante === "primaer" && "bg-accent-strong px-3 py-2 text-white hover:opacity-90",
        variante === "gefahr" && "border border-crit/40 bg-surface px-3 py-2 text-crit hover:bg-crit/10",
        variante === "link" && "px-0.5 py-1 text-[12.5px] text-accent-strong underline underline-offset-2",
        className,
      )}
    />
  )
}
